import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { draftsV2Api, type DraftListItem } from '@/lib/api/v2/drafts';
import { useProgramStore } from '@/stores/programStore';
import { toast } from 'sonner';
import { FileEdit, Trash2, Search, Clock, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const DraftsListV2 = () => {
  const navigate = useNavigate();
  const { loadDraft } = useProgramStore();
  
  const [drafts, setDrafts] = useState<DraftListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [draftToDelete, setDraftToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch drafts on mount
  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await draftsV2Api.getAll({
        page: 1,
        limit: 50,
        sortBy: 'lastSaved',
        sortOrder: 'desc',
      });
      
      if (response.success) {
        setDrafts(response.data.drafts);
      } else {
        throw new Error(response.message || 'Failed to load drafts');
      }
    } catch (err: any) {
      console.error('Error fetching drafts:', err);
      setError(err.message || 'Failed to load drafts');
      toast.error('Failed to load drafts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueEditing = async (draftId: string) => {
    try {
      await loadDraft(draftId);
      navigate('/programs-v2/create');
    } catch (error) {
      // Error is handled in the store
    }
  };

  const handleDeleteClick = (draftId: string) => {
    setDraftToDelete(draftId);
  };

  const handleDeleteConfirm = async () => {
    if (!draftToDelete) return;
    
    setIsDeleting(true);
    try {
      await draftsV2Api.delete(draftToDelete);
      
      // Remove from list
      setDrafts((prev) => prev.filter((d) => d._id !== draftToDelete));
      
      toast.success('Draft deleted successfully');
      setDraftToDelete(null);
    } catch (error: any) {
      console.error('Error deleting draft:', error);
      toast.error(error.message || 'Failed to delete draft');
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter drafts by search query
  const filteredDrafts = drafts.filter((draft) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      draft.title?.toLowerCase().includes(query) ||
      draft._id.toLowerCase().includes(query)
    );
  });

  // Loading state
  if (isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (error) {
    return <ErrorState message={error} onRetry={fetchDrafts} />;
  }

  // Empty state
  if (drafts.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search drafts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Badge variant="secondary" className="text-xs">
          {drafts.length} draft{drafts.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Drafts list */}
      {filteredDrafts.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No drafts match your search.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredDrafts.map((draft) => (
            <DraftCard
              key={draft._id}
              draft={draft}
              onContinue={() => handleContinueEditing(draft._id)}
              onDelete={() => handleDeleteClick(draft._id)}
            />
          ))}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!draftToDelete} onOpenChange={() => setDraftToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Draft?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this draft? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============================================================================
// DRAFT CARD COMPONENT
// ============================================================================

interface DraftCardProps {
  draft: DraftListItem;
  onContinue: () => void;
  onDelete: () => void;
}

const DraftCard = ({ draft, onContinue, onDelete }: DraftCardProps) => {
  // Safely format the date
  const formatLastSaved = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'recently';
      }
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  return (
    <Card className="flex flex-col hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm line-clamp-2 break-words">
              {draft.title || 'Untitled Draft'}
            </h3>
            <div className="mt-1">
              <Badge variant="outline" className="text-xs">
                {draft.draftId || draft._id}
              </Badge>
            </div>
          </div>
          <Badge variant="secondary" className="text-xs shrink-0">
            {draft.status === 'in-progress' ? 'In Progress' : draft.status || 'Ready'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pb-3">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>
            Saved {formatLastSaved(draft.lastSaved || draft.updatedAt)}
          </span>
        </div>
      </CardContent>

      <CardFooter className="pt-0 flex gap-2">
        <Button
          onClick={onContinue}
          variant="default"
          size="sm"
          className="flex-1"
        >
          <FileEdit className="h-3 w-3 mr-1" />
          Continue
        </Button>
        <Button
          onClick={onDelete}
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </CardFooter>
    </Card>
  );
};

// ============================================================================
// LOADING STATE
// ============================================================================

const LoadingState = () => (
  <div className="space-y-4">
    <div className="flex items-center gap-2">
      <Skeleton className="h-10 flex-1 max-w-sm" />
      <Skeleton className="h-6 w-20" />
    </div>
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i} className="flex flex-col">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          </CardHeader>
          <CardContent className="pb-3">
            <Skeleton className="h-3 w-40" />
          </CardContent>
          <CardFooter className="pt-0 flex gap-2">
            <Skeleton className="h-9 flex-1" />
            <Skeleton className="h-9 w-9" />
          </CardFooter>
        </Card>
      ))}
    </div>
  </div>
);

// ============================================================================
// ERROR STATE
// ============================================================================

interface ErrorStateProps {
  message: string;
  onRetry: () => void;
}

const ErrorState = ({ message, onRetry }: ErrorStateProps) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <AlertCircle className="h-12 w-12 text-destructive mb-4" />
    <h3 className="text-lg font-semibold">Failed to Load Drafts</h3>
    <p className="text-sm text-muted-foreground mt-2 max-w-md">{message}</p>
    <Button onClick={onRetry} variant="outline" className="mt-4">
      Try Again
    </Button>
  </div>
);

// ============================================================================
// EMPTY STATE
// ============================================================================

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="rounded-full bg-muted p-4 mb-4">
      <FileEdit className="h-8 w-8 text-muted-foreground" />
    </div>
    <h3 className="text-lg font-semibold">No Drafts Yet</h3>
    <p className="text-sm text-muted-foreground mt-2 max-w-md">
      Start creating a program to save your first draft
    </p>
  </div>
);

